import { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { EASE } from "@/components/Motion";
import { FIRM } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About Us", id: "about" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/insights", label: "Insights", id: "insights" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const onScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const lenis = window.__lenis;
    if (open) {
      document.body.style.overflow = "hidden";
      lenis?.stop?.();
    } else {
      document.body.style.overflow = "";
      lenis?.start?.();
    }
    return () => {
      document.body.style.overflow = "";
      lenis?.start?.();
    };
  }, [open]);

  const solid = scrolled && !open;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          solid
            ? "border-b border-navy/10 bg-white/95 shadow-[0_10px_40px_-20px_rgba(16,36,58,0.25)] backdrop-blur-md"
            : "border-b border-white/10 bg-navy/40 backdrop-blur-sm"
        }`}
      >
        <div className="container-x flex h-[76px] items-center justify-between lg:h-[84px]">
          <Link
            to="/"
            data-testid="nav-link-logo"
            aria-label="Steven Philips & Associates Limited — Home"
            className="shrink-0"
          >
            <Logo light={!solid} />
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `group relative text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                    solid
                      ? isActive
                        ? "text-navy"
                        : "text-charcoal/70 hover:text-navy"
                      : isActive
                        ? "text-white"
                        : "text-white/70 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      aria-hidden="true"
                      className={`absolute -bottom-2 left-0 h-px bg-gold transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-schedule"
              className="btn-gold !py-3"
            >
              Schedule a Consultation
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </nav>

          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className={`relative z-50 flex h-12 w-12 flex-col items-center justify-center gap-[7px] lg:hidden ${
              open ? "text-gold" : solid ? "text-navy" : "text-white"
            }`}
          >
            <span
              aria-hidden="true"
              className={`h-[1.5px] w-7 bg-current transition-all duration-300 ${
                open ? "translate-y-[4.25px] rotate-45" : ""
              }`}
            />
            <span
              aria-hidden="true"
              className={`h-[1.5px] w-7 bg-current transition-all duration-300 ${
                open ? "-translate-y-[4.25px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 top-0 z-40 flex flex-col bg-navy-deep lg:hidden"
          >
            <div className="grain absolute inset-0" aria-hidden="true" />
            <div className="container-x relative flex h-full flex-col justify-center gap-2 pt-20">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 + i * 0.07, duration: 0.7, ease: EASE }}
                  className="overflow-hidden border-b border-white/10"
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `block py-5 font-serif text-4xl font-medium transition-colors ${
                        isActive ? "text-gold" : "text-white hover:text-gold"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.7, ease: EASE }}
                className="mt-10 flex flex-col gap-6"
              >
                <Link
                  to="/contact"
                  data-testid="mobile-nav-cta-schedule"
                  className="btn-gold w-fit"
                >
                  Schedule a Consultation
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <div className="space-y-1 text-sm text-white/60">
                  <a
                    href={FIRM.phoneHref}
                    data-testid="mobile-menu-phone"
                    className="block hover:text-gold"
                  >
                    {FIRM.phone}
                  </a>
                  <a
                    href={`mailto:${FIRM.email}`}
                    data-testid="mobile-menu-email"
                    className="block hover:text-gold"
                  >
                    {FIRM.email}
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
